import { validate as isUuid } from 'uuid';
import { z } from 'zod';
import {
  resolveEntityWorkspaceSlug,
  type WorkspaceRef
} from 'v5/core/app-scope/entityWorkspaceRouting';
import { appRouting } from 'v5/core/routing/appRouting';
import { myRouting } from 'v5/features/core/routing';

const CALLOUT_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function titleToCalloutSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function isValidCalloutSlug(slug: string): boolean {
  if (!slug || slug.length > 255) {
    return false;
  }
  if (isUuid(slug)) {
    return false;
  }
  return CALLOUT_SLUG_PATTERN.test(slug);
}

export function normalizeCalloutSlugForSubmit(slug: string): string {
  return titleToCalloutSlug(slug.trim()) || 'callout';
}

export function resolveCalloutSlugForSubmit(
  slug: string,
  title: string,
  slugTouched: boolean
): string {
  const trimmedSlug = slug.trim();
  if (slugTouched && trimmedSlug) {
    return titleToCalloutSlug(trimmedSlug) || 'callout';
  }
  return titleToCalloutSlug(title) || 'callout';
}

export function resolveCalloutSlugFromForm(
  slug: string,
  title: string,
  slugTouched = Boolean(slug.trim())
): string {
  return resolveCalloutSlugForSubmit(slug, title, slugTouched);
}

export function isCalloutSlugValidForSubmit(
  slug: string,
  title: string,
  slugTouched = Boolean(slug.trim())
): boolean {
  const resolved = resolveCalloutSlugForSubmit(slug, title, slugTouched);
  return isValidCalloutSlug(resolved);
}

export const calloutSlugSchema = z
  .string()
  .min(1)
  .max(255)
  .refine(isValidCalloutSlug, {
    message: 'Invalid slug format'
  });

export function getCalloutPublicPathIdentifier(callout: {
  slug?: string | null;
  public_id?: string;
}): string | undefined {
  return callout.slug ?? callout.public_id;
}

export type GetCalloutPublicUrlOptions = {
  workspaces?: WorkspaceRef[];
};

export function getCalloutPublicUrlPrefix(
  organizationSlug: string,
  enterpriseWorkspaceSlug?: string | null
): string {
  if (enterpriseWorkspaceSlug) {
    return appRouting
      .org(organizationSlug)
      .workspace(enterpriseWorkspaceSlug)
      .callouts.publicCallout('');
  }

  return myRouting.forWorkspace(organizationSlug).callouts().publicCallout('');
}

/** Short prefix shown in slug form fields — omits `/app/:org`. */
export function getCalloutSlugFieldPrefix(
  enterpriseWorkspaceSlug?: string | null
): string {
  if (enterpriseWorkspaceSlug) {
    return `/w/${enterpriseWorkspaceSlug}/p/callouts/`;
  }

  return '/p/callouts/';
}

export function getCalloutPublicUrl(
  organizationSlug: string,
  callout: {
    slug?: string | null;
    public_id?: string;
    enterprise_workspace_id?: number | null;
  },
  options?: GetCalloutPublicUrlOptions
): string | undefined {
  const identifier = getCalloutPublicPathIdentifier(callout);

  if (!identifier) {
    return undefined;
  }

  const normalizedIdentifier = identifier.toLowerCase();
  const enterpriseWorkspaceSlug =
    callout.enterprise_workspace_id != null && callout.slug
      ? resolveEntityWorkspaceSlug(callout, options?.workspaces)
      : undefined;

  if (enterpriseWorkspaceSlug) {
    return `${getCalloutPublicUrlPrefix(organizationSlug, enterpriseWorkspaceSlug)}${normalizedIdentifier}`;
  }

  return `${getCalloutPublicUrlPrefix(organizationSlug)}${normalizedIdentifier}`;
}

export function matchesCalloutPublicPathIdentifier(
  callout: { slug?: string | null; public_id?: string },
  identifier: string
): boolean {
  const normalizedIdentifier = identifier.toLowerCase();
  return (
    callout.slug?.toLowerCase() === normalizedIdentifier ||
    callout.public_id?.toLowerCase() === normalizedIdentifier
  );
}
